import React, { createContext, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

const AccessibilityContext = createContext();

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};

export const AccessibilityProvider = ({ children }) => {
  const { user, updateProfile } = useAuth();

  // Persist one accessibility flag with a single PUT /auth/profile. The
  // backend merges nested preference objects, so only the changed subtree is sent.
  const updateAccessibilityPreference = async (setting, value) => {
    try {
      await updateProfile({
        preferences: {
          accessibility: {
            ...(user?.preferences?.accessibility || {}),
            [setting]: value
          }
        }
      });
    } catch (error) {
      console.error('Failed to update accessibility preference:', error);
      throw error;
    }
  };

  // Apply accessibility settings to the document
  useEffect(() => {
    if (!user?.preferences?.accessibility) return;

    const { highContrast, largeText, reducedMotion, screenReader } = user.preferences.accessibility;

    // High Contrast Mode
    if (highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }

    // Large Text
    if (largeText) {
      document.body.classList.add('large-text');
    } else {
      document.body.classList.remove('large-text');
    }

    // Reduced Motion
    if (reducedMotion) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }

    // Screen Reader Optimizations
    if (screenReader) {
      document.body.classList.add('screen-reader-optimized');
    } else {
      document.body.classList.remove('screen-reader-optimized');
    }

    // Add CSS custom properties for dynamic styling
    document.documentElement.style.setProperty('--accessibility-high-contrast', highContrast ? '1' : '0');
    document.documentElement.style.setProperty('--accessibility-large-text', largeText ? '1' : '0');
    document.documentElement.style.setProperty('--accessibility-reduced-motion', reducedMotion ? '1' : '0');
    document.documentElement.style.setProperty('--accessibility-screen-reader', screenReader ? '1' : '0');

  }, [user?.preferences?.accessibility]);

  // Apply accessibility CSS when component mounts
  useEffect(() => {
    // Create and inject accessibility CSS
    const style = document.createElement('style');
    style.id = 'accessibility-styles';
    style.textContent = `
      /* High Contrast Mode */
      .high-contrast {
        filter: contrast(150%) brightness(110%);
      }
      
      .high-contrast .MuiCard-root {
        border: 2px solid #000 !important;
        background-color: #fff !important;
        color: #000 !important;
      }
      
      .high-contrast .MuiButton-root {
        border: 2px solid #000 !important;
        font-weight: bold !important;
      }
      
      .high-contrast .MuiButton-contained {
        background-color: #000 !important;
        color: #fff !important;
      }
      
      .high-contrast .MuiButton-outlined {
        background-color: #fff !important;
        color: #000 !important;
      }

      /* Large Text */
      .large-text {
        font-size: 120% !important;
      }
      
      .large-text * {
        font-size: inherit !important;
      }
      
      .large-text .MuiTypography-h1 { font-size: 3.6rem !important; }
      .large-text .MuiTypography-h2 { font-size: 2.7rem !important; }
      .large-text .MuiTypography-h3 { font-size: 2.4rem !important; }
      .large-text .MuiTypography-h4 { font-size: 2.1rem !important; }
      .large-text .MuiTypography-h5 { font-size: 1.8rem !important; }
      .large-text .MuiTypography-h6 { font-size: 1.5rem !important; }
      .large-text .MuiTypography-body1 { font-size: 1.2rem !important; }
      .large-text .MuiTypography-body2 { font-size: 1.05rem !important; }
      .large-text .MuiButton-root { font-size: 1.1rem !important; }
      .large-text .MuiTextField-root input { font-size: 1.2rem !important; }

      /* Reduced Motion */
      .reduced-motion,
      .reduced-motion * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
      
      .reduced-motion .MuiCircularProgress-root {
        animation: none !important;
      }
      
      .reduced-motion .MuiLinearProgress-root .MuiLinearProgress-bar {
        animation: none !important;
      }

      /* Screen Reader Optimizations */
      .screen-reader-optimized {
        /* Ensure focus indicators are visible */
      }
      
      .screen-reader-optimized *:focus {
        outline: 3px solid #005fcc !important;
        outline-offset: 2px !important;
      }
      
      .screen-reader-optimized .MuiButton-root:focus {
        outline: 3px solid #005fcc !important;
        outline-offset: 2px !important;
      }
      
      .screen-reader-optimized .MuiTextField-root:focus-within {
        outline: 3px solid #005fcc !important;
        outline-offset: 2px !important;
      }

      /* Skip to content link for screen readers */
      .skip-to-content {
        position: absolute;
        top: -40px;
        left: 6px;
        background: #000;
        color: #fff;
        padding: 8px;
        text-decoration: none;
        z-index: 9999;
        border-radius: 4px;
      }
      
      .skip-to-content:focus {
        top: 6px;
      }

      /* Ensure sufficient color contrast */
      .high-contrast .MuiChip-root {
        border: 1px solid #000 !important;
        font-weight: bold !important;
      }
      
      .high-contrast .MuiTableCell-root {
        border: 1px solid #000 !important;
      }
      
      .high-contrast .MuiAlert-root {
        border: 2px solid #000 !important;
        font-weight: bold !important;
      }
    `;

    // Remove existing accessibility styles if they exist
    const existingStyle = document.getElementById('accessibility-styles');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Add new styles
    document.head.appendChild(style);

    // Cleanup function
    return () => {
      const styleElement = document.getElementById('accessibility-styles');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  const contextValue = {
    isHighContrast: user?.preferences?.accessibility?.highContrast || false,
    isLargeText: user?.preferences?.accessibility?.largeText || false,
    isReducedMotion: user?.preferences?.accessibility?.reducedMotion || false,
    isScreenReaderOptimized: user?.preferences?.accessibility?.screenReader || false,
    updateAccessibilityPreference,
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {/* Skip to content link for screen readers */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      <div id="main-content">
        {children}
      </div>
    </AccessibilityContext.Provider>
  );
};

export default AccessibilityProvider;
